import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  ApolloLink,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import * as Sentry from '@sentry/react';

import isAuthError from './isAuthError';
import appToastAdd from '../actions/appToastAdd';
import errorAction from '../actions/error';
import sessionClearAction from '../actions/sessionClear';
import store from '../constants/store';
import selectSessionToken from '../selectors/sessionToken';
import getGraphQLRequestHeaders from '../utils/getGraphQLRequestHeaders';

const httpLink = createHttpLink({
  uri: `/graphql`,
});
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 30000,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error) => {
      if (!error) return false;
      const graphQLErrors = error?.result?.errors;
      return !isAuthError(graphQLErrors);
    },
  },
});

const middlewareLink = new ApolloLink((operation, forward) => {
  const storeState = store.getState();
  const sessionToken = selectSessionToken(storeState);

  if (!sessionToken) return;

  operation.setContext({
    headers: {
      ...getGraphQLRequestHeaders(sessionToken),
    },
    requestToken: sessionToken,
  });

  return forward(operation);
});

const timingSafeEqual = (a: string, b: string): boolean => {
  const enc = new TextEncoder();
  const aBytes = enc.encode(a);
  const bBytes = enc.encode(b);
  if (aBytes.length !== bBytes.length) return false;
  let result = 0;
  for (let i = 0; i < aBytes.length; i++) {
    result |= aBytes[i] ^ bBytes[i];
  }
  return result === 0;
};

const errorLink = onError(({ networkError = {}, graphQLErrors, operation }) => {
  if (isAuthError(graphQLErrors)) {
    const requestToken = operation.getContext().requestToken;
    const currentToken = selectSessionToken(store.getState());  
    if (requestToken && currentToken && timingSafeEqual(requestToken, currentToken)) {
      store.dispatch(sessionClearAction());
    }
    return;
  }

  // An operation can take responsibility for its own failure: a red toast over
  // a screen that is working as intended buries the errors that are not.
  if (operation.getContext().errorHandledLocally) return;

  // Handle Error - appToast for DEV, Sentry for PROD
  if (Array.isArray(graphQLErrors)) {
    Array.from(graphQLErrors).map((error, i) => {
      if (process.env.NODE_ENV !== 'development') {
        const scope = new Sentry.Scope();
        scope.setTag('operation', operation?.operationName);
        scope.setTag('variables', JSON.stringify(operation?.variables));
        scope.setFingerprint([
          '{{ default }}',
          String(error?.message),
          String(operation?.operationName),
        ]);

        Sentry.captureException(new Error(error?.message), scope);
      } else {
        console.error('operation', operation.operationName);
        console.error('variables', JSON.stringify(operation.variables));

        store.dispatch(
          appToastAdd({
            durationMilliseconds: 4000,
            message: error?.message,
            title: 'GRAPHQL_ERROR',
            toastKey: `error_${i}_${Date.now()}`,
          }),
        );
      }
    });
  }

  store.dispatch(
    errorAction({
      error: graphQLErrors,
      title: 'GraphQL Error',
      description: String(graphQLErrors),
    }),
  );
});

const link = middlewareLink
  .concat(retryLink)
  .concat(errorLink)
  .concat(httpLink);

export default new ApolloClient({
  link,
  cache: new InMemoryCache(),
});